// The evaluations list. One screen for every role — the API returns an agent their own
// rows, a supervisor their brand's, and QMS/admin everything, so the only role-dependent
// thing here is which FILTERS are worth showing.

import { useEffect, useMemo, useState } from 'react';
import * as api from '../api';
import type { DateAxis, EvaluatedAgentOption, EvaluatorOption, Meta, PanelUser } from '../types';
import { useAsync, usePeriodPicker } from '../hooks';
import { Avatar, Empty, Icon, Loading, Note, PageHead, Panel, Pill, ScorePill } from '../ui';
import { STATUS_LABELS, STATUS_TONE, shortDateTime } from '../format';
import PeriodBar from './PeriodBar';

const PAGE = 50;

// The period window can sit on the CALL's time (every report's axis, the default) or on the
// EVALUATION's — when it was filed. The second exists for one question: "how much did this
// specialist do today". Calls are scored after the fact (on the live box 13 of 642 rows
// share a day with their call), so a day window on call_at with an evaluator picked would
// answer a different question and read as "nothing today".
const AXIS_LABEL: Record<DateAxis, string> = { call: 'Çağrı tarihi', created: 'Değerlendirme tarihi' };

export default function Evaluations({ user, meta, brand }: {
  user: PanelUser; meta: Meta | null;
  /** The topbar's global brand filter (App.tsx) — this screen no longer owns one, so
   *  moving between screens keeps the brand the reader picked. */
  brand: string;
}) {
  // The list is a browse/triage surface, not a report: default to EVERY in-scope call
  // (newest first, already paginated), with the period picker as an optional filter. A QMS
  // specialist scores recorded calls after the fact, so a fresh evaluation's call_at is
  // usually a prior week/month — pinning the list to the current week hid those rows from
  // everyone who did not create them (a supervisor most of all).
  const picker = usePeriodPicker(meta, 'all');
  const [status, setStatus] = useState('');
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [evaluatorId, setEvaluatorId] = useState('');
  const [dateBy, setDateBy] = useState<DateAxis>('call');
  const [offset, setOffset] = useState(0);
  // The toggle is hidden in 'all' mode, so its stored value must not act there either —
  // the axis now drives the sort and the time column, and a hidden switch that silently
  // reordered "Tümü" by evaluation time would be a second invisible filter.
  const axis: DateAxis = picker.kind === 'all' ? 'call' : dateBy;

  const isAgent = user.role === 'agent';
  // Staff only: an agent already reads the evaluator off the Değerlendiren column, and the
  // roster route is closed to them (the server answers 403 — no point asking).
  const evaluators = useAsync<EvaluatorOption[]>(
    () => (isAgent ? Promise.resolve([]) : api.getEvaluators()),
    [isAgent],
  );
  // The agent filter, the evaluator filter's twin (owner 2026-09-21, Task Board #124/#125).
  // Same roster shape — everyone with a row in scope, deactivated included — and, like the
  // Raporlar roster, narrowed under the topbar brand filter and sorted in Turkish.
  const [agentId, setAgentId] = useState('');
  const evaluatedAgents = useAsync<EvaluatedAgentOption[]>(
    () => (isAgent ? Promise.resolve([]) : api.getEvaluatedAgents()),
    [isAgent],
  );
  const agentOptions = useMemo(() => {
    const rows = (evaluatedAgents.data ?? []).filter((a) => !brand || a.brandKey === brand);
    return [...rows].sort((a, b) => a.displayName.localeCompare(b.displayName, 'tr'));
  }, [evaluatedAgents.data, brand]);
  // A pick the brand filter no longer contains would keep narrowing the list to an agent
  // the dropdown cannot show — drop it rather than run an invisible filter.
  useEffect(() => {
    if (agentId && !agentOptions.some((a) => String(a.id) === agentId)) setAgentId('');
  }, [agentOptions, agentId]);

  // Switching to a period (or week/month kind) with fewer rows must return to page 1 —
  // otherwise a leftover offset from a bigger period asks for LIMIT 50 OFFSET 100 and the
  // screen sticks on a spurious empty page (the paginator hides because total < offset).
  useEffect(() => { setOffset(0); }, [picker.id, brand]);

  const state = useAsync(
    () => api.listEvaluations({
      // '' in 'all' mode → qs() drops it → the server applies no period window.
      period: picker.id || undefined, brand: brand || undefined, status: status || undefined,
      agentId: agentId ? Number(agentId) : undefined,
      evaluatorId: evaluatorId ? Number(evaluatorId) : undefined,
      // The server's default is the call axis; only the other one is worth a query key.
      dateBy: axis === 'created' ? axis : undefined,
      criticalOnly: criticalOnly || undefined, limit: PAGE, offset,
    }),
    [picker.id, brand, status, agentId, evaluatorId, axis, criticalOnly, offset],
  );

  const periodLabel = picker.kind === 'all'
    ? 'Tüm dönemler'
    : (picker.options.find((o) => o.id === picker.id)?.label ?? ' ');
  const hint = isAgent ? 'kendi çağrıların'
    : picker.kind === 'all' ? 'tüm çağrılar'
    : axis === 'created' ? 'seçili dönemde değerlendirilen çağrılar'
    : 'seçili dönemdeki tüm çağrılar';

  return (
    <>
      <PageHead
        icon="fact_check"
        title={isAgent ? 'Değerlendirmelerim' : 'Değerlendirmeler'}
        subtitle={periodLabel}
        right={(
          <PeriodBar
            picker={picker}
            allowAll
            // The axis only means something once there IS a window; in 'all' mode the
            // toggle would be a control that changes nothing.
            right={picker.kind !== 'all' && !isAgent ? (
              <div className="seg" title="Dönem penceresi hangi tarihe uygulansın">
                {(Object.keys(AXIS_LABEL) as DateAxis[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={dateBy === k ? 'on' : ''}
                    onClick={() => { setDateBy(k); setOffset(0); }}
                  >
                    {AXIS_LABEL[k]}
                  </button>
                ))}
              </div>
            ) : null}
          />
        )}
      />

      <Panel
        icon="playlist_add_check"
        title={state.data ? `${state.data.total} kayıt` : 'Kayıtlar'}
        hint={hint}
        right={(
          <div className="filters">
            {!isAgent && agentOptions.length > 0 ? (
              <select
                className="inp auto"
                value={agentId}
                onChange={(e) => { setAgentId(e.target.value); setOffset(0); }}
              >
                <option value="">Tüm temsilciler</option>
                {/* One template string → ONE text node, so the i18n "(pasif)" regex sees
                    the whole label (three adjacent JSX expressions would be three nodes). */}
                {agentOptions.map((a) => (
                  <option key={a.id} value={String(a.id)}>
                    {`${a.displayName}${brand ? '' : ` · ${a.brandKey}`}${a.active ? '' : ' (pasif)'}`}
                  </option>
                ))}
              </select>
            ) : null}
            {!isAgent && evaluators.data && evaluators.data.length > 0 ? (
              <select
                className="inp auto"
                value={evaluatorId}
                onChange={(e) => { setEvaluatorId(e.target.value); setOffset(0); }}
              >
                <option value="">Tüm değerlendiriciler</option>
                {evaluators.data.map((ev) => (
                  <option key={ev.id} value={String(ev.id)}>{ev.displayName}</option>
                ))}
              </select>
            ) : null}
            <select className="inp auto" value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }}>
              <option value="">Tüm durumlar</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <div className="seg">
              <button
                type="button"
                className={criticalOnly ? 'on' : ''}
                onClick={() => { setCriticalOnly(!criticalOnly); setOffset(0); }}
              >
                Sadece kritik hatalı
              </button>
            </div>
          </div>
        )}
      >
        {state.error ? <div className="panel-body"><Note error>{state.error}</Note></div> : null}
        {state.loading && !state.data ? <Loading /> : null}
        {state.data && state.data.evaluations.length === 0 ? (
          <Empty>Bu filtrelerle değerlendirme bulunamadı.</Empty>
        ) : null}
        {state.data && state.data.evaluations.length > 0 ? (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  {/* The time column follows the axis: under "Değerlendirme tarihi" the
                      window and the sort run on createdAt, so that is the timestamp that
                      belongs beside the period heading — showing callAt there put 13.09
                      rows under a 14–20.09 title and read as a broken filter. */}
                  <th>{axis === 'created' ? 'Değerlendirme saati' : 'Saat'}</th>
                  {isAgent ? null : <th>Agent</th>}
                  <th>Kullanıcı ID</th>
                  <th>Liste</th><th>Arama tipi</th><th>Kayıt</th>
                  <th>Kalite</th><th>Durum</th>
                  {isAgent ? <th>Değerlendiren</th> : null}
                  <th />
                </tr>
              </thead>
              <tbody>
                {state.data.evaluations.map((e) => (
                  <tr key={e.id} className={e.hasCritical ? 'crit' : undefined}>
                    <td className="tnum">{shortDateTime(axis === 'created' ? e.createdAt : e.callAt)}</td>
                    {isAgent ? null : (
                      <td>
                        <div className="who">
                          <Avatar name={e.agentName} kind={e.hasCritical ? 'bad' : 'neutral'} />
                          <span className="nm">{e.agentName}</span>
                        </div>
                      </td>
                    )}
                    <td className="tnum" translate="no">{e.playerUserId ?? '—'}</td>
                    <td>{e.listName ? <Pill tone="neu">{e.listName}</Pill> : '—'}</td>
                    <td>{e.callType || '—'}</td>
                    {/* A call may carry up to three recordings — the count is what tells the
                        reader there is more to hear than the first player on the detail. */}
                    <td>{e.audioCount > 0
                      ? (
                        <span className="row" style={{ gap: 4 }}>
                          <Icon name="mic" style={{ color: 'var(--accent)', fontSize: 18 }} />
                          {e.audioCount > 1 ? <span className="tnum">{e.audioCount}</span> : null}
                        </span>
                      )
                      : <span className="faint">—</span>}</td>
                    <td><ScorePill score={e.finalScore} hasCritical={e.hasCritical} /></td>
                    <td><Pill tone={STATUS_TONE[e.status]}>{STATUS_LABELS[e.status]}</Pill></td>
                    {isAgent ? <td>{e.evaluatorName}</td> : null}
                    <td className="r">
                      {/* A real hash link, not a button: right-click → "open in new tab",
                          middle/cmd-click and copy-link all work (owner, Task Board #118/#119).
                          A plain click still routes in-app via hashchange. */}
                      <a className="btn soft sm" href={`#/evaluation/${e.id}`}>
                        {isAgent ? 'Detay' : 'İncele'}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {state.data && state.data.total > PAGE ? (
          <div className="pager">
            <button className="btn sm" type="button" disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}><Icon name="arrow_back" /><span>Önceki</span></button>
            <span>
              {offset + 1}–{Math.min(offset + PAGE, state.data.total)} / {state.data.total}
            </span>
            <button className="btn sm" type="button" disabled={offset + PAGE >= state.data.total}
              onClick={() => setOffset(offset + PAGE)}><span>Sonraki</span><Icon name="arrow_forward" /></button>
          </div>
        ) : null}
      </Panel>
    </>
  );
}
